import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Task } from './types';
import { fetchTaskAssignees } from './api';

interface TasksBoardProps {
  tasks: Task[];
  onCreateTask: (payload: Record<string, any>) => Promise<void>;
  onUpdateTask: (taskId: string, payload: Record<string, any>) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onReorder: (nextTasks: Task[], items: Array<{ id: string; status: string; boardOrder: number }>) => void;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

const columns = [
  { id: 'todo', title: 'Att göra', color: '#e3f2fd', borderColor: '#1976d2' },
  { id: 'doing', title: 'Pågår', color: '#fff3e0', borderColor: '#f57c00' },
  { id: 'done', title: 'Klart', color: '#e8f5e9', borderColor: '#388e3c' },
] as const;

const priorityColor = (priority: number) => {
  if (priority === 1) return 'error';
  if (priority === 2) return 'warning';
  return 'default';
};

const TasksBoard: React.FC<TasksBoardProps> = ({ tasks, onCreateTask, onUpdateTask, onDeleteTask, onReorder, canCreate = true, canUpdate = true, canDelete = true }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'Process',
    priority: 3,
    status: 'todo',
    assigneeName: '',
  });
  const [knownAssignees, setKnownAssignees] = useState<string[]>([]);

  // Load known assignees when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      fetchTaskAssignees()
        .then(setKnownAssignees)
        .catch(() => setKnownAssignees([]));
    }
  }, [dialogOpen]);

  const grouped = useMemo(() => {
    const initial: Record<string, Task[]> = { todo: [], doing: [], done: [] };
    tasks.forEach((task) => {
      (initial[task.status] || initial.todo).push(task);
    });
    Object.keys(initial).forEach((status) => {
      initial[status] = initial[status].sort((a, b) => {
        const aOrder = Number.isFinite(a.boardOrder) ? a.boardOrder : 0;
        const bOrder = Number.isFinite(b.boardOrder) ? b.boardOrder : 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      });
    });
    return initial;
  }, [tasks]);

  const openNewTask = (status: string = 'todo') => {
    if (!canCreate) return;
    setEditingTask(null);
    setForm({ title: '', description: '', category: 'Process', priority: 3, status, assigneeName: '' });
    setDialogOpen(true);
  };

  const openEditTask = (task: Task) => {
    if (!canUpdate) return;
    setEditingTask(task);
    setForm({
      title: task.title,
      description: task.description || '',
      category: task.category,
      priority: task.priority,
      status: task.status,
      assigneeName: task.assigneeName || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    const payload = {
      title: form.title.trim(),
      description: form.description,
      category: form.category,
      priority: form.priority,
      status: form.status,
      assigneeName: form.assigneeName.trim(),
    };
    if (editingTask) {
      if (!canUpdate) return;
      await onUpdateTask(editingTask._id, payload);
    } else {
      if (!canCreate) return;
      await onCreateTask(payload);
    }
    setDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    if (!editingTask) return;
    await onDeleteTask(editingTask._id);
    setDialogOpen(false);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!canUpdate) return;
    if (!result.destination) return;
    const sourceStatus = result.source.droppableId;
    const destStatus = result.destination.droppableId;
    if (!grouped[sourceStatus] || !grouped[destStatus]) return;

    const sourceList = Array.from(grouped[sourceStatus]);
    const destList = sourceStatus === destStatus ? sourceList : Array.from(grouped[destStatus]);

    const [moved] = sourceList.splice(result.source.index, 1);
    destList.splice(result.destination.index, 0, moved);

    const nextByStatus: Record<string, Task[]> = {
      ...grouped,
      [sourceStatus]: sourceList,
      [destStatus]: destList,
    };

    const updateMap = new Map<string, { status: string; boardOrder: number }>();
    Object.entries(nextByStatus).forEach(([status, list]) => {
      list.forEach((task, index) => {
        updateMap.set(task._id, { status, boardOrder: (index + 1) * 10 });
      });
    });

    const updates: Array<{ id: string; status: string; boardOrder: number }> = [];
    const nextTasks = tasks.map((task) => {
      const update = updateMap.get(task._id);
      if (!update) return task;
      updates.push({ id: task._id, status: update.status, boardOrder: update.boardOrder });
      return { ...task, status: update.status as any, boardOrder: update.boardOrder };
    });

    onReorder(nextTasks, updates);
  };

  return (
    <>
      {!tasks.length ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Inga uppgifter ännu
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Klicka på "Ny uppgift" ovan för att skapa din första uppgift.
          </Typography>
        </Paper>
      ) : (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
              {columns.map((column) => (
                <Droppable key={column.id} droppableId={column.id}>
                  {(provided, snapshot) => (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        minWidth: 280,
                        width: 320,
                        backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'grey.50',
                        borderTop: `3px solid ${column.borderColor}`,
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ 
                        backgroundColor: column.color, 
                        px: 2, 
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {column.title}
                          <Chip 
                            size="small" 
                            label={grouped[column.id]?.length || 0} 
                            sx={{ 
                              height: 22, 
                              minWidth: 28,
                              fontWeight: 600,
                              backgroundColor: 'white',
                            }} 
                          />
                        </Typography>
                        {canCreate && (
                          <IconButton size="small" onClick={() => openNewTask(column.id)} sx={{ backgroundColor: 'white', '&:hover': { backgroundColor: 'grey.100' } }}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Box sx={{ p: 1.5, minHeight: 100, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(grouped[column.id] || []).map((task, index) => (
                          <Draggable key={task._id} draggableId={task._id} index={index} isDragDisabled={!canUpdate}>
                            {(dragProvided, dragSnapshot) => (
                              <Paper
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                onClick={() => openEditTask(task)}
                                elevation={dragSnapshot.isDragging ? 4 : 1}
                                sx={{
                                  p: 1.5,
                                  cursor: canUpdate ? 'pointer' : 'default',
                                  borderLeft: `4px solid`,
                                  borderLeftColor: task.priority === 1 ? 'error.main' : task.priority === 2 ? 'warning.main' : 'grey.300',
                                  backgroundColor: dragSnapshot.isDragging ? 'grey.100' : 'background.paper',
                                  transition: 'box-shadow 0.2s, background-color 0.2s',
                                  '&:hover': canUpdate ? {
                                    backgroundColor: 'grey.50',
                                  } : {},
                                }}
                              >
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                  {task.title}
                                </Typography>
                                {task.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    {task.description.length > 60 ? task.description.substring(0, 60) + '...' : task.description}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <Chip size="small" label={`P${task.priority}`} color={priorityColor(task.priority)} sx={{ height: 20, fontSize: '0.7rem' }} />
                                  <Chip size="small" label={task.category} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  {task.assigneeName && (
                                    <Chip 
                                      size="small" 
                                      icon={<PersonIcon sx={{ fontSize: '0.85rem !important' }} />}
                                      label={task.assigneeName} 
                                      color="primary"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }} 
                                    />
                                  )}
                                </Box>
                              </Paper>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    </Paper>
                  )}
                </Droppable>
              ))}
            </Box>
          </DragDropContext>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? 'Redigera uppgift' : 'Ny uppgift'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Titel"
            fullWidth
            margin="dense"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />
          <TextField
            label="Beskrivning"
            fullWidth
            margin="dense"
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              select
              label="Kategori"
              value={form.category}
              onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
              fullWidth
            >
              {['Content', 'Testing', 'Process', 'Release'].map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Prioritet"
              value={form.priority}
              onChange={(e) => setForm((prev) => ({ ...prev, priority: Number(e.target.value) }))}
              fullWidth
            >
              {[1, 2, 3, 4].map((priority) => (
                <MenuItem key={priority} value={priority}>{priority}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
              fullWidth
            >
              {['todo', 'doing', 'done'].map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
            Tilldelad
          </Typography>
          <Autocomplete
            freeSolo
            options={knownAssignees}
            value={form.assigneeName}
            onChange={(_, newValue) => setForm((prev) => ({ ...prev, assigneeName: newValue || '' }))}
            onInputChange={(_, newInputValue) => setForm((prev) => ({ ...prev, assigneeName: newInputValue }))}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Skriv ett namn"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <PersonIcon sx={{ color: 'action.active', mr: 1 }} fontSize="small" />,
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          {editingTask && canDelete && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
              Ta bort
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDialogOpen(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSave}>Spara</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TasksBoard;
